// Planner, Connections and Projects.
import { useState } from "react";
import { T, Tf } from "../lib/i18n";
import { COLOR_BY_FIRST, Head, Icon, Locked, Orb, Pill, useStudio } from "../components/ui";
import { LADDER, PLANS, PROJECTS } from "../data/sample";
import { SendToPlanner } from "../components/SendToPlanner";
import { cleanError, graph, m365Available, openLink, useGraph, type Plan, type Task } from "../lib/m365";

export function Planner() {
  if (m365Available()) return <LivePlanner />;
  return <SamplePlanner />;
}

function LivePlanner() {
  const { go, m365 } = useStudio();
  const on = m365.connected("planner");
  const [plan, setPlan] = useState("all");
  // "All my tasks" is what's assigned to you; a plan shows every task in it, whoever has it.
  const mine = useGraph<Task[]>("planner.tasks", on && plan === "all");
  const planTasks = useGraph<{ plan: string; tasks: Task[] }>("planner.planTasks", on && plan !== "all", { planId: plan });
  const { data: allPlans } = useGraph<Plan[]>("planner.plans", on);
  const { data, error, loading, reload } = plan === "all" ? mine : { ...planTasks, data: planTasks.data?.tasks ?? null };
  const [adding, setAdding] = useState(false);
  const [importing, setImporting] = useState(false);
  const plans = (allPlans ?? []).map((p) => [p.id, p.title] as const);
  const shown = data ?? [];
  const cols: [string, (t: Task) => boolean][] = [["Not started", (t) => t.percent === 0], ["In progress", (t) => t.percent > 0 && t.percent < 100], ["Done", (t) => t.percent === 100]];
  const overdue = shown.filter((t) => t.percent < 100 && t.due && new Date(t.due) < new Date()).length;
  return (
    <>
      <Head title="Planner" lead="Your Microsoft Planner tasks, across every plan you're in. VERA turns plans into tasks with owners and dates, and you confirm each one.">
        {on && <button className="btn" onClick={() => setImporting(true)}><Icon name="grid" size={16} />{" "}{T("Send from Excel")}</button>}
        {on && <button className="btn" onClick={() => setAdding(true)}><Icon name="plus" size={16} />{" "}{T("Add task")}</button>}
        <button className="btn primary" onClick={() => go("workspace", { ask: "Make an Excel action log for the launch plan, with exactly these columns: Action ID, Action / Deliverable, Owner, Source / Forum, Status, Priority, Date Opened, Due Date (dates as YYYY-MM-DD), so I can send it to Planner with Send from Excel." })}>{T("Plan with VERA")}</button>
      </Head>
      {!on ? (
        <div className="card stack" style={{ alignItems: "flex-start", gap: 10 }}><h2>{T("Connect Microsoft Planner")}</h2><p className="mute">{T("See your real tasks here, and let VERA create the ones you approve.")}</p><button className="btn primary" onClick={() => go("connections")}>{T("Open Connections")}</button></div>
      ) : loading && !data ? <p className="faint">{T("Loading your tasks…")}</p> : error ? <p style={{ color: "var(--bad)" }}>{error}</p> : (
        <>
          <div className="row">
            <button className={`chip ${plan === "all" ? "on" : ""}`} onClick={() => setPlan("all")}>{T("Assigned to me")}</button>
            {plans.map(([id, title]) => <button key={id} className={`chip ${plan === id ? "on" : ""}`} onClick={() => setPlan(id)}>{title}</button>)}
          </div>
          <div className="grid g3">{cols.map(([name, f]) => {
            const tasks = shown.filter(f);
            return (
              <div className="card stack" style={{ gap: 8 }} key={name}>
                <div className="row" style={{ justifyContent: "space-between" }}><b>{name}</b><span className="faint num">{tasks.length}</span></div>
                {tasks.slice(0, 30).map((t) => (
                  <button className="today-item linkish" key={t.id} onClick={() => openLink(`https://tasks.office.com/`)}>
                    <b>{t.title}</b>
                    <div className="faint" style={{ fontSize: 12, marginTop: 4 }}>{plan === "all" ? t.plan : [t.bucket, t.assignees?.length ? t.assignees.join(", ") : "Not assigned"].filter(Boolean).join(" · ")}{t.due ? Tf(" · due {date}", { date: new Date(t.due).toLocaleDateString([], { month: "short", day: "numeric" }) }) : ""}{t.percent < 100 && t.due && new Date(t.due) < new Date() ? T(" · overdue") : ""}</div>
                  </button>))}
                {tasks.length > 30 && <p className="faint">and {tasks.length - 30}{" "}{T("more in Planner")}</p>}{!tasks.length && <p className="faint">{plan === "all" && name === "Not started" && !shown.length && plans.length ? T("Nothing is assigned to you. Choose a plan above to see all its tasks.") : T("Nothing here.")}</p>}
              </div>);
          })}</div>
          {overdue > 0 && <div className="notice calm"><Orb size={18} style={{ marginTop: 2 }} /><span className="mute">{T("VERA noticed:")}{" "}<b style={{ color: "var(--text)" }}>{overdue} {plan === "all" ? T("of your tasks") : T("tasks in this plan")} {overdue === 1 ? "is" : "are"} overdue.</b>{" "}{T("Want help deciding what to move?")}</span></div>}
          <span className="live">{T("Live from Microsoft Planner")}</span>
        </>
      )}
      {importing && <SendToPlanner onClose={() => setImporting(false)} onDone={() => void reload()} />}
      {adding && <AddTask onClose={(made) => { setAdding(false); if (made) void reload(); }} />}
    </>
  );
}

function AddTask({ onClose }: { onClose: (made: boolean) => void }) {
  const { toast } = useStudio();
  const { data: plans, error: plansError } = useGraph<Plan[]>("planner.plans", true);
  const [planId, setPlanId] = useState("");
  const [title, setTitle] = useState("");
  const [due, setDue] = useState("");
  const [mine, setMine] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const pick = planId || plans?.[0]?.id || "";
  async function save() {
    setBusy(true); setError("");
    try {
      const r = await graph<{ cancelled?: boolean }>("planner.createTask", { planId: pick, planTitle: plans?.find((p) => p.id === pick)?.title, title, due: due || undefined, assignToMe: mine });
      if (r.cancelled) { setBusy(false); return; }
      toast(T("Task created in Planner."));
      onClose(true);
    } catch (e) { setError(cleanError(e)); setBusy(false); }
  }
  return (
    <div className="quick-back" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(false); }}>
      <div className="card quick" role="dialog" aria-modal="true" aria-label={T("New Planner task")} style={{ maxWidth: 520 }}>
        <h2 style={{ fontSize: 19 }}>{T("New Planner task")}</h2>
        <label className="fld"><span>{T("Plan")}</span>
          <select value={pick} onChange={(e) => setPlanId(e.target.value)} disabled={!plans?.length}>
            {!plans ? <option>{T("Loading your plans…")}</option> : !plans.length ? <option>{T("No plans found")}</option> : plans.map((p) => <option key={p.id} value={p.id}>{p.title} · {p.group}</option>)}
          </select></label>
        {plansError && <p style={{ color: "var(--bad)", fontSize: 13 }}>{plansError}</p>}
        <label className="fld"><span>{T("Task")}</span><input value={title} onChange={(e) => setTitle(e.target.value)} placeholder={T("Submit Consumer 2.0 for App Store review")} /></label>
        <div className="row" style={{ gap: 12 }}>
          <label className="fld" style={{ flex: 1 }}><span>{T("Due (optional)")}</span><input type="date" value={due} onChange={(e) => setDue(e.target.value)} /></label>
          <label className="row" style={{ gap: 8, marginTop: 18, fontSize: 13 }}><input type="checkbox" checked={mine} onChange={(e) => setMine(e.target.checked)} />{" "}{T("Assign to me")}</label>
        </div>
        {error && <p style={{ color: "var(--bad)", fontSize: 13 }} role="alert">{error}</p>}
        <div className="row" style={{ justifyContent: "flex-end", gap: 8 }}><button className="btn" onClick={() => onClose(false)}>{T("Cancel")}</button><button className="btn primary" disabled={!pick || !title.trim() || busy} onClick={() => void save()}>{busy ? T("Creating…") : T("Create task")}</button></div>
      </div>
    </div>
  );
}

function SamplePlanner() {
  const { profile, go } = useStudio();
  const plans = PLANS.filter((p) => profile.scopes.includes(p.scope));
  const [planId, setPlanId] = useState(plans[0]?.id);
  const plan = plans.find((p) => p.id === planId) ?? plans[0];
  return (
    <>
      <Head title="Planner" lead="Your Microsoft Planner, inside VERA. VERA turns plans into tasks with owners and dates, nudges what's overdue, and keeps everyone in sync across Planner, Teams and Outlook.">

        <button className="btn primary" onClick={() => go("workspace", { ask: "Make an Excel action log for the launch plan, with exactly these columns: Action ID, Action / Deliverable, Owner, Source / Forum, Status, Priority, Date Opened, Due Date (dates as YYYY-MM-DD), so I can send it to Planner with Send from Excel." })}>{T("Plan with VERA")}</button>
      </Head>
      <div className="row">{plans.map((p) => <button key={p.id} className={`chip ${p.id === plan?.id ? "on" : ""}`} onClick={() => setPlanId(p.id)}>{p.name}</button>)}</div>
      {plan ? (
        <>
          <div className="grid g3">{(Object.entries(plan.buckets) as [string, [string, string, string][]][]).map(([bucket, tasks]) => (
            <div className="card stack" style={{ gap: 8 }} key={bucket}>
              <div className="row" style={{ justifyContent: "space-between" }}><b>{bucket}</b><span className="faint num">{tasks.length}</span></div>
              {tasks.map(([t, who, due]) => (
                <div className="today-item" key={t}>
                  <div className="row" style={{ justifyContent: "space-between", flexWrap: "nowrap" }}><b>{t}</b>{bucket === T("Done") && <Pill tone="ok">{T("Done")}</Pill>}</div>
                  <div className="row" style={{ marginTop: 6 }}><span className="av" style={{ width: 22, height: 22, fontSize: 10, background: COLOR_BY_FIRST[who] ?? "#555" }}>{who[0]}</span><span className="faint" style={{ fontSize: 12 }}>{who}{" "}{T("· due")}{" "}{due}</span></div>
                </div>))}
              {!tasks.length && <p className="faint">{T("Nothing here yet.")}</p>}
            </div>))}
          </div>
          <div className="notice calm"><Orb size={18} style={{ marginTop: 2 }} /><span className="mute">{T("VERA noticed:")}{" "}<b style={{ color: "var(--text)" }}>{plan.noticed}</b></span></div>
          <p className="faint" style={{ fontSize: 12.5 }}>{T("Sample data. Connect Microsoft Planner in Connections to see your real plans.")}</p>
        </>
      ) : <p className="faint">{T("No plans shared with you yet.")}</p>}
    </>
  );
}

export { Connections } from "./Connections";

export function Projects() {
  const { profile, pending, clearPending, go } = useStudio();
  const [open, setOpen] = useState<string | null>(() => { const p = pending?.project ?? null; if (pending) clearPending(); return p; });
  const p = PROJECTS.find((x) => x.id === open);
  if (p) {
    const full = profile.projects === "full";
    return (
      <>
        <div className="head"><div><button className="btn" onClick={() => setOpen(null)} style={{ marginBottom: 12 }}>← All projects</button><h1>{p.name}</h1><p className="mute">For {p.who} · <Pill tone={p.status[0]}>{p.status[1]}</Pill></p></div>
          <div className="row">{full ? <><button className="btn" onClick={() => go("workspace", { project: p.id, ask: `What's blocking ${p.short} and how do we fix it?` })}>Fix something</button></> : <Locked />}</div></div>
        <div className="grid g2"><div className="card stack"><span className="label">Live</span><p>{p.live}</p></div><div className="card stack"><span className="label">Preview</span><p>{p.preview}</p></div></div>
        <div className="split">
          <div className="card"><h2 style={{ marginBottom: 8 }}>Capability ladder</h2>
            <div className="list">{LADDER.map((s, i) => {
              const blocked = p.blocked && i === p.step + 1;
              return <div className="li" key={s}><Pill tone={i <= p.step ? "ok" : blocked ? "bad" : "info"}>{i <= p.step ? "Reached" : blocked ? "Blocked" : "Not yet"}</Pill><div className="grow"><b>{s}</b></div></div>;
            })}</div>
            {p.blocked && <div className="notice" style={{ marginTop: 12 }}><span>Blocked by: <b>{p.next}</b></span></div>}
          </div>
          <div className="card stack"><span className="label">Code</span><div className="row"><Icon name="repo" /><b>{p.repo}</b></div>
            <p className="faint">Changes from VERA always arrive as a branch and pull request. Main is never changed directly.</p>
            <button className="btn" onClick={() => go("workspace", { project: p.id })}>Ask VERA about {p.short}</button></div>
        </div>
      </>
    );
  }
  return (
    <>
      <Head title="Projects" lead="Every product, described by the highest step it has actually reached. Updated by the team by hand; not a live feed."><span className="sampletag">Updated by hand</span></Head>
      <div className="grid g3">{PROJECTS.map((x) => (
        <button className="card stack" style={{ textAlign: "left", gap: 12 }} key={x.id} onClick={() => setOpen(x.id)}>
          <div className="row" style={{ justifyContent: "space-between", width: "100%" }}><h2 style={{ fontSize: 19 }}>{x.name}</h2><Pill tone={x.status[0]}>{x.status[1]}</Pill></div>
          <p className="faint" style={{ fontSize: 12.5 }}>For {x.who}</p>
          <div><div className="ladder">{LADDER.map((_, i) => <span key={i} className={`${i <= x.step ? "on" : ""} ${x.blocked && i === x.step + 1 ? "blk" : ""}`} />)}</div>
            <p className="faint" style={{ fontSize: 11.5, marginTop: 6 }}>{x.step < 0 ? "Not started" : LADDER[x.step]}{x.blocked ? ` · blocked before ${LADDER[x.step + 1]}` : ""}</p></div>
          <p style={{ fontSize: 13 }}><span className="faint">Next:</span> {x.next}</p>
        </button>))}
      </div>
    </>
  );
}
